import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Eye,
  UserCog,
  Shield,
  Clock,
  Search,
  XCircle,
  AlertTriangle,
  History,
  LogOut
} from 'lucide-react';

const API = getApiBaseUrl();

export default function AdminImpersonation() {
  const { token, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState(30);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch users for impersonation
  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/users?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || data);
      }
    } catch (err) {
      console.error('Users fetch error:', err);
    }
  }, [token]);

  // Fetch impersonation sessions
  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/impersonate/sessions?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
        // Check for active session
        const active = data.find(s => s.active && s.admin_user_id === user.id);
        setActiveSession(active || null);
      }
    } catch (err) {
      console.error('Sessions fetch error:', err);
    }
  }, [token, user]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchSessions()]);
      setLoading(false);
    };
    loadData();
  }, [fetchUsers, fetchSessions]);

  const startImpersonation = async () => {
    if (!selectedUser || !reason) {
      toast.error('Please select a user and provide a reason');
      return;
    }

    try {
      const res = await fetch(`${API}/api/admin/impersonate/start`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          target_user_id: selectedUser.id,
          reason,
          duration_minutes: duration
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Impersonating ${selectedUser.email}`);
        
        // Store impersonation token
        localStorage.setItem('impersonation_token', data.impersonation_token);
        localStorage.setItem('impersonation_session', JSON.stringify(data));
        localStorage.setItem('original_token', token);
        
        // Redirect to appropriate dashboard based on role
        const targetRole = data.target_user.role;
        if (targetRole === 'tenant') {
          window.location.href = '/tenant/dashboard';
        } else if (targetRole === 'landlord' || targetRole === 'landlord_admin') {
          window.location.href = '/landlord/dashboard';
        } else if (targetRole === 'contractor') {
          window.location.href = '/contractor/dashboard';
        } else {
          window.location.href = '/';
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to start impersonation');
      }
    } catch (err) {
      toast.error('Error starting impersonation');
    }
  };

  const endImpersonation = async (sessionId) => {
    try {
      const res = await fetch(`${API}/api/admin/impersonate/end/${sessionId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Impersonation session ended');
        localStorage.removeItem('impersonation_token');
        localStorage.removeItem('impersonation_session');
        localStorage.removeItem('original_token');
        fetchSessions();
      } else {
        toast.error('Failed to end session');
      }
    } catch (err) {
      toast.error('Error ending session');
    }
  };

  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      u.email?.toLowerCase().includes(query) ||
      u.first_name?.toLowerCase().includes(query) ||
      u.last_name?.toLowerCase().includes(query) ||
      u.role?.toLowerCase().includes(query)
    );
  }).filter(u => u.role !== 'super_admin'); // Can't impersonate super admins

  const roleColors = {
    tenant: 'bg-blue-100 text-blue-800',
    landlord: 'bg-green-100 text-green-800',
    landlord_admin: 'bg-green-100 text-green-800',
    contractor: 'bg-orange-100 text-orange-800',
    support_agent: 'bg-purple-100 text-purple-800',
    super_admin: 'bg-red-100 text-red-800'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Warning Banner */}
      <div className="bg-yellow-500 text-yellow-900 px-4 py-2 text-center">
        <div className="flex items-center justify-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          <span className="font-medium">
            Admin Impersonation - All actions are logged and audited
          </span>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <UserCog className="h-8 w-8 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">User Impersonation</h1>
              <p className="text-gray-500">View the platform as any user for support purposes</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Active Session Alert */}
        {activeSession && (
          <Card className="mb-6 border-purple-200 bg-purple-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Eye className="h-5 w-5 text-purple-600" />
                  <div>
                    <span className="font-medium">Active Impersonation: </span>
                    <span>{activeSession.target_email}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">
                    Expires: {new Date(activeSession.expires_at).toLocaleString()}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => endImpersonation(activeSession.id)}
                  >
                    <LogOut className="h-4 w-4 mr-2" /> End Session
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Selection */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5" /> Select User to Impersonate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Input
                    placeholder="Search by email, name, or role..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full"
                  />
                </div>
                
                <div className="max-h-96 overflow-y-auto space-y-2">
                  {filteredUsers.map((u) => (
                    <div
                      key={u.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedUser?.id === u.id 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedUser(u)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">
                            {u.first_name} {u.last_name}
                          </div>
                          <div className="text-sm text-gray-500">{u.email}</div>
                        </div>
                        <Badge className={roleColors[u.role] || 'bg-gray-100'}>
                          {u.role}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {filteredUsers.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No users found
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Impersonation Form */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" /> Start Impersonation
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedUser ? (
                  <div className="space-y-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-500">Selected User</div>
                      <div className="font-medium">{selectedUser.email}</div>
                      <Badge className={roleColors[selectedUser.role] || ''}>
                        {selectedUser.role}
                      </Badge>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Reason for Impersonation *
                      </label>
                      <Textarea
                        placeholder="Enter the reason for this impersonation..."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={3}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Duration (minutes)
                      </label>
                      <Input
                        type="number"
                        min={5}
                        max={60}
                        value={duration}
                        onChange={(e) => setDuration(parseInt(e.target.value))}
                      />
                      <p className="text-xs text-gray-500 mt-1">Maximum 60 minutes</p>
                    </div>

                    <Button
                      className="w-full"
                      onClick={startImpersonation}
                      disabled={!reason}
                    >
                      <Eye className="h-4 w-4 mr-2" /> Start Impersonation
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Select a user from the list to impersonate
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Sessions */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" /> Recent Sessions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {sessions.slice(0, 10).map((session) => (
                    <div key={session.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{session.target_email}</span>
                        {session.active ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline">Ended</Badge>
                        )}
                      </div>
                      <div className="text-gray-500 text-xs">
                        <Clock className="h-3 w-3 inline mr-1" />
                        {new Date(session.started_at).toLocaleString()}
                      </div>
                      <div className="text-gray-600 text-xs mt-1">
                        Reason: {session.reason}
                      </div>
                      {session.active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-2 text-red-600 hover:text-red-700"
                          onClick={() => endImpersonation(session.id)}
                        >
                          <XCircle className="h-3 w-3 mr-1" /> End
                        </Button>
                      )}
                    </div>
                  ))}
                  {sessions.length === 0 && (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      No recent sessions
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
